<script lang="ts" setup>
import { AccessControl, AC_MODES, AC_CREATION_LEVEL } from '@/models/AccessControl';
import { Planungsgeschaeft } from '@/models/Planungsgeschaeft';
import { computed } from 'vue';
import { useAuthUser } from '@/helpers/globals';
import { capitalize, formatDayjsIf } from '@/helpers/tools';

const props = defineProps<{
    record: AccessControl;
    geschaeft: Planungsgeschaeft;
}>();

const emit = defineEmits<{
    (e: 'deleted', rec: AccessControl): void;
    (e: 'modechanged', mode: string, ac: AccessControl): void;
}>();

const iconName = computed(() => (props.record.user_id !== null ? 'user' : 'home'));
const editable = computed(
    () =>
        props.record?.locked === false &&
        (useAuthUser()?.id === props.geschaeft?.creator_id || useAuthUser()?.isRoleAdmin)
);

async function onDeleteClick() {
    emit('deleted', props.record);
}

async function toggleWrite(checked: boolean): Promise<void> {
    emit('modechanged', checked ? AC_MODES.MODE_READ_WRITE : AC_MODES.MODE_READ, props.record);
}
</script>

<template>
    <div class="accard w-full p-1rem">
        <div class="flex flex-row w-full gap-1rem">
            <ktg-icon :name="iconName"></ktg-icon>
            <span
                v-if="
                    props.record.creation_level === AC_CREATION_LEVEL.CREATION_LEVEL_GEMEINDE && props.record.accepted
                "
                class="ktg-subtitle-03 pr-0_5rem"
                >{{ props.record.name }}</span
            ><span v-else class="ktg-subtitle-03 disabled pr-0_5rem">{{ $t('berechtigter_nutzer') }}</span>
            <ktg-pill v-if="useAuthUser()?.id === props.record?.user_id" appearance="running" class="ml-1rem">{{
                $t('my_account')
            }}</ktg-pill>
            <ktg-pill
                v-if="props.record?.accepted === null || !props.record.accepted.isValid()"
                appearance="warning"
                >{{ $t('not_accepted_yet') }}</ktg-pill
            >
            <div v-if="editable" class="flex flex-1 flex-grow-1 justify-content-end">
                <ktg-table-button appearance="danger" icon="trash-delete" @click.prevent="onDeleteClick">{{
                    capitalize($t('remove'))
                }}</ktg-table-button>
            </div>
        </div>
        <ktg-line class="mt-1rem mb-1rem"></ktg-line>
        <div class="ktg-body-small lighter flex flex-row w-full">
            {{ $t('hinzugefuegt_am') }} {{ formatDayjsIf(props.record.created, 'DD.MM.YYYY')
            }}<span class="ml-2rem">{{ props.record?.email }}</span>

            <div v-if="editable" class="flex flex-1 flex-grow-1 justify-content-end gap-0_5rem">
                <span >{{ $t('schreibrechte') }}</span>
                <ktg-toggle
                    :checked="props.record.mode === 'rw'"
                    input-id="writetoggle"
                    @change="toggleWrite($event.target?.checked)"
                ></ktg-toggle>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.accard {
    background-color: var(--ktg-color-neutral-01);

    .disabled {
        color: var(--ktg-color-neutral-04);
    }

    .lighter {
        color: #767676;
    }

    ktg-icon {
        color: var(--ktg-color-link);
    }
}
</style>
